import { useState, useEffect, useRef, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Edit, ListCheck, CalendarDays, Gift, Image, Eye, EyeOff, Filter, Search, RotateCcw, X } from "lucide-react";

import useDocumentTitle from '../../hooks/useDocumentTitle';
import ShimmerLoader from '../../hooks/ShimmerLoader';
import {creatLoyalityReward,creatLoyalityRewardParent,fetchstores,deactivateLoyaltyReward,approveLoyaltyReward  } from "../../services/api";
import Pagination from "../../hooks/Pagination";
import { fetchLoyaltyRewards, fetchLoyaltyProgramsForRewards } from "../../store/loyaltySlices/loyaltyRewardsSlice";

import toastr from 'toastr';
import RejectRewardModal from "./RejectRewardModal";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/light.css";
import dayjs from "dayjs";

import { DateInput } from "@mantine/dates";
import MultiSelect from "../../hooks/MultiSelect";

const ADMIN_ONLY = ['rewardtype','rewardvalue','max_reward_value','custvalue','ownervalue','maxdiscount_slabs','discount_enabled','goerone_discount_type'];
// Status cell: approval badge for store-created rewards, plain Active/Inactive otherwise.

// reward.discount_enabled: 0 = GoerOne, 1 = Store, 2 = Both. Rows saved before the column existed have null.
const hasGoeroneDiscount = (item) =>
  item.discount_enabled == null || item.discount_enabled === ''
    ? Number(item.custvalue) > 0
    : [0, 2].includes(Number(item.discount_enabled));
const hasStoreDiscount = (item) =>
  item.discount_enabled == null || item.discount_enabled === ''
    ? Number(item.store_disc_value) > 0
    : [1, 2].includes(Number(item.discount_enabled));

const ACTION_BTN = 'h-9 min-w-[2.25rem] px-2 rounded-xl bg-amber-50 text-[#d89005] hover:bg-amber-100 inline-flex items-center justify-center gap-1 text-xs font-medium transition-colors';

// store_info comes from the API as "id:name, id:name" - show just the names as chips.
function StoreChips({ info }) {
  if (!info) return <span className="text-gray-400">—</span>;
  const names = String(info).split(', ').map((p) => (p.includes(':') ? p.split(':').slice(1).join(':') : p)).filter(Boolean);
  return (
    <div className="flex flex-wrap gap-1 max-w-[220px]">
      {names.map((n, i) => (
        <span key={`${n}-${i}`} className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-xs">{n}</span>
      ))}
    </div>
  );
}

// Show the first 3 program names; the rest are revealed on click.
const PROGRAM_LIMIT = 3;
function ProgramChips({ programs }) {
  const [showAll, setShowAll] = useState(false);
  const list = Array.isArray(programs) ? programs : [];
  const visible = showAll ? list : list.slice(0, PROGRAM_LIMIT);
  const hidden = list.length - PROGRAM_LIMIT;

  return (
    <div className="flex flex-wrap items-center gap-1 max-w-[220px]">
      {visible.map((p, i) => (
        <span key={`${p.id}-${i}`} className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs">{p.name}</span>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="px-2 py-0.5 rounded-full text-xs font-medium text-[#d89005] hover:bg-amber-50 transition-colors"
        >
          {showAll ? 'Show less' : `+${hidden} more`}
        </button>
      )}
    </div>
  );
}

function RewardStatus({ item }) {
  if (!item.is_store_created) {
    const active = Number(item.is_active) === 1;
    return (
      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>
        {active ? 'Active' : 'Inactive'}
      </span>
    );
  }

  const approvalStatus = Number(item.approval_status);
  let badge;

  if (approvalStatus === 0) {
    badge = {
      text: 'Pending approval',
      cls: 'bg-yellow-100 text-yellow-800',
    };
  } else if (approvalStatus === 2) {
    badge = {
      text: 'Rejected',
      cls: 'bg-red-100 text-red-800',
    };
  } else if (approvalStatus === 1 && Number(item.is_active) === 1) {
    badge = {
      text: 'Approved & Active',
      cls: 'bg-green-100 text-green-800',
    };
  } else {
    badge = {
      text: 'Approved & Inactive',
      cls: 'bg-gray-100 text-gray-700',
    };
  }

  return (
    <div>
      <span
        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}
      >
        {badge.text}
      </span>

      {approvalStatus === 2 && item.reject_reason && (
        <div className="mt-1 text-xs text-red-600 max-w-[220px] whitespace-normal">
          Reason: {item.reject_reason}
        </div>
      )}
      {item.admin_fields_missing && approvalStatus !== 1 && <div className="mt-1 text-xs text-orange-600">Cashback/discount values not set</div>}
    </div>
  );
}

function CreatedBy({ item }) {
  return (
    <div className="text-xs text-gray-500">
      {item.is_store_created ? `Created by store${item.created_by_store_name ? `: ${item.created_by_store_name}` : ''}` : 'Created by admin'}
    </div>
  );
}

// ---- Form styling helpers (presentation only) ----
const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#f4a406] focus:border-[#f4a406] disabled:bg-gray-100 disabled:cursor-not-allowed';
const slabInputCls = 'px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f4a406] focus:border-[#f4a406]';

function Section({ title, hint, children }) {
  return (
    <section className="rounded-xl border border-gray-200 p-4">
      <div className="mb-3">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function Field({ label, required, hint, children, className = '' }) {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {children}
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  );
}

// Radio group drawn as a two-option toggle (still plain radio inputs, so handleChange works as before).
function Segmented({ name, value, onChange, options }) {
  return (
    <div className="inline-flex rounded-xl border border-gray-300 bg-gray-50 p-0.5">
      {options.map((o) => {
        const active = String(value) === String(o.value);
        return (
          <label
            key={o.value}
            className={`cursor-pointer px-4 py-1.5 text-sm rounded-lg transition-colors focus-within:ring-2 focus-within:ring-[#f4a406] ${
              active ? 'bg-[#f4a406] text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <input type="radio" name={name} value={o.value} checked={active} onChange={onChange} className="sr-only" />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}

function Switch({ checked, onChange, label, hint, disabled = false }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <span className="relative mt-0.5 inline-flex shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={disabled ? undefined : onChange}
          disabled={disabled}
          aria-label={label}
          className="peer sr-only"
        />
        <span className={`w-9 h-5 rounded-full transition-colors ${checked ? 'bg-[#f4a406]' : 'bg-gray-300'} ${disabled ? 'opacity-60' : ''} peer-focus-visible:ring-2 peer-focus-visible:ring-[#f4a406] peer-focus-visible:ring-offset-1`} />
        <span className={`absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </span>
      <span>
        <span className="block text-sm font-medium text-gray-900">{label}</span>
        {hint && <span className="block text-xs text-gray-500">{hint}</span>}
      </span>
    </label>
  );
}

export default function LoyaltyRewards() {
  useDocumentTitle('Welcome to Admin | Loyalty Card');
  const qs = new URLSearchParams(window.location.search); 
  const dispatch = useDispatch();

  const [createdBy, setCreatedBy] = useState(qs.get('created_by') || 'all');
  const [statusFilter, setStatusFilter] = useState(qs.get('status') || 'all');
  const [keyword, setKeyword] = useState(qs.get('keyword') || ''); // applied search (store name / reward name)
  const [filterDraft, setFilterDraft] = useState({
    createdBy: qs.get('created_by') || 'all',
    status: qs.get('status') || 'all',
    keyword: qs.get('keyword') || '',
  });
  const [showFilters, setShowFilters] = useState(false); // hidden by default
  const [rejectTarget, setRejectTarget] = useState(null);

  const { rewards: loyalty_card, programs: loyalty_program, loading, error, totalPages } = useSelector((state) => state.loyaltyRewards);
  const [storelist, setStoreList] = useState([]);
  const [storeError, setStoreError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fileInputRef = useRef(null);
  const wrapperRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);

  const perPage = 20;
const fetchLoyaltyCard = (perPage, currentPage) =>
  dispatch(fetchLoyaltyRewards({ perPage, page: currentPage, created_by: createdBy, status: statusFilter, keyword }));

const handleDraft = (e) => setFilterDraft((prev) => ({ ...prev, [e.target.name]: e.target.value }));
const handleApplyFilters = (e) => {
  e.preventDefault();
  setCurrentPage(1);
  setCreatedBy(filterDraft.createdBy);
  setStatusFilter(filterDraft.status);
  setKeyword(filterDraft.keyword.trim());
};
const handleResetFilters = () => {
  setFilterDraft({ createdBy: 'all', status: 'all', keyword: '' });
  setCurrentPage(1);
  setCreatedBy('all');
  setStatusFilter('all');
  setKeyword('');
};
const activeFilters = (createdBy !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0) + (keyword ? 1 : 0);

  
  const handleDeactivate = async (item) => {
    const confirmed = window.confirm(
      `Are you sure you want to deactivate "${item.reward_name}"?`
    );

    if (!confirmed) return;

    try {
      const result = await deactivateLoyaltyReward(item.reward_id);

      if (!result?.status?.success) {
        toastr.error(result?.message || 'Failed to deactivate reward.');
        return;
      }

      toastr.success(result.message || 'Reward deactivated successfully.');

      // Refresh the existing list so the updated status appears.
      fetchLoyaltyCard(perPage, currentPage);
    } catch (error) {
      toastr.error(
        error?.response?.data?.message || 'Failed to deactivate reward.'
      );
    }
  };


  const fetchStore = async (keyword = '') => {
    try {
      const response = await fetchstores(25, 1, keyword);
      const data = response?.data;
      // Support both paginated Laravel responses and plain array responses.
      const rows = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.data?.data)
          ? data.data.data
          : Array.isArray(data)
            ? data
            : [];

      if (keyword.trim().length > 2) {
        setSuggestions(rows);
        setStoreOptions(rows); // Bind the fetched search results to the dropdown.
      } else {
        setStoreList(rows);
        if (!keyword.trim()) setStoreOptions(rows);
      }
      setStoreError('');
      return rows;
    } catch (err) {
      setStoreError(err?.message || 'Failed to load stores');
      setSuggestions([]);
      setStoreOptions([]);
      return [];
    }
  };
  useEffect(() => {
    dispatch(fetchLoyaltyProgramsForRewards());
    fetchStore()
    fetchLoyaltyCard(perPage, currentPage);
  }, [currentPage, createdBy, statusFilter, keyword, dispatch]);

  const initialFormData = {
    stores_id: [],
    reward_id: 0,
    parent_id: 0,
    program_id: [],
    is_active: "1",
    reward_name: '',
    description: "",
    start_date: null,
    points_required: 0,
    end_date: null,
    reward_logo: null,
    dealtype: 0,
    rewardtype: 0,
    dealvalue: 0,
    ownervalue: 0,
    custvalue: 0,
    ordervalue: 0,
    rewardvalue: 0,
    max_reward_value: 0,
    maxdiscount_slabs: [{ to: '', max_discount: '' }],
    // Customer discount by GoerOne (admin-only) and by Store - both optional
    goerone_disc_enabled: 1,
    goerone_discount_type: 0,   // 0 = percentage, 1 = fixed
    store_disc_enabled: 0,
    store_disc_type: 0,
    store_disc_value: 0,
    store_maxdiscount_slabs: [{ to: '', max_discount: '' }]
  };
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  // null while adding; while editing it holds the flags the API sent for that row
  // (can_edit, lock_stores, is_store_created, approval_status, reject_reason, created_by_store_name).
  const [editMeta, setEditMeta] = useState(null);

  // Store users work inside /store-panel (same check the api.js interceptor uses);
  // the API also flags every row with viewer_is_store.
  const viewerIsStore =
    window.location.pathname.startsWith("/store-panel") || loyalty_card.some((r) => r.viewer_is_store);
  const isEditing = !!editMeta;
  const readOnly = isEditing && editMeta.can_edit === false;      // store opening an admin-created reward
  const lockStores = isEditing && !!editMeta.lock_stores;         // admin opening a store-created reward

  const handleOpen = () => {
    setOpen(!open)
    setSelected([]);
    setEditMeta(null);
    setFormData(initialFormData)
  };

  // const handleChange = (e, field = '') => {
  //   let name = '';
  //   let value = '';
  //   if (e.target) {
  //     name = e.target.name;
  //     value = e.target.value;
  //   } else {
  //     name = field;//field.name;
  //     value = e;//.toLocaleString("sv-SE")//toISOString()
  //   }


  //   setFormData(prev => ({
  //     ...prev,
  //     [name]: value
  //   }));
  // };

  const handleChange = (e, field = '') => {
    let name = '';
    let value = '';

    if (e.target) {
      name = e.target.name;

      if (e.target.multiple) {
        // Handle multiple select values
        value = Array.from(e.target.options)
          .filter(option => option.selected)
          .map(option => option.value);
      } else {
        value = e.target.value;
      }
    } else {
      name = field;
      value = e;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const updateSlab = (index, field, value, key = 'maxdiscount_slabs') =>
  setFormData(prev => ({
    ...prev,
    [key]: prev[key].map((s, i) => (i === index ? { ...s, [field]: value } : s)),
  }));

  const addSlab = (key = 'maxdiscount_slabs') =>
    setFormData(prev => ({ ...prev, [key]: [...prev[key], { to: '', max_discount: '' }] }));

  const removeSlab = (index, key = 'maxdiscount_slabs') =>
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));

  const toEditableSlabs = (item, key = 'maxdiscount_slabs') => {
  let slabs = item[key];
  if (typeof slabs === 'string') { try { slabs = JSON.parse(slabs); } catch { slabs = null; } }
  if (Array.isArray(slabs) && slabs.length) {
    return slabs.map(s => ({ to: s.to ?? '', max_discount: s.max_discount }));
  }
  return [{ to: '', max_discount: '' }];
};

  // Shared slab rules for the GoerOne and Store max-discount slabs. Returns an error message or null.
  const validateSlabs = (slabs = [], { fixedCap = 0, capLabel = 'Total value', prefix = '' } = {}) => {
    let prevTo = 0;
    for (let i = 0; i < slabs.length; i++) {
      const n = i + 1;
      const disc = Number(slabs[i].max_discount);
      if (!disc || disc <= 0) return `${prefix}Slab ${n}: enter a maximum discount`;
      if (fixedCap > 0 && disc > fixedCap) {
        return `${prefix}Slab ${n}: max discount cannot exceed ${capLabel} (${fixedCap}) for fixed deals`;
      }
      if (i < slabs.length - 1) {
        const to = Number(slabs[i].to);
        if (!to || to <= prevTo) return `${prefix}Slab ${n}: "up to" must be greater than ${prevTo}`;
        prevTo = to;
      }
    }
    return null;
  };

  const handleChangeImg = (e, field = '') => {
    const { name, type, files } = e.target;
    if (type === "file" && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          [name]: file, // actual file
          [`${name}_preview`]: reader.result, // preview URL
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e, { approveAfter = false } = {}) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    toastr.options = {
      closeButton: true,
      progressBar: true,
      timeOut: 3000,
      positionClass: 'toast-top-center'
    };

    if (readOnly) {
      toastr.error('This reward has been created by admin. If you want to change it, please contact admin.', { timeOut: 3000 });
      return false
    }
    // Store users are always mapped to their own store, so they don't pick one.
    if (!viewerIsStore && selected.length === 0) {
      toastr.error('Select at least one store Name', { timeOut: 2000 });
      return false
    }
    if (formData.program_id === '') {
      toastr.error('Select Program Name', { timeOut: 2000 });
      return false
    }
    if (formData.reward_name === '') {
      toastr.error('Enter reward number', { timeOut: 2000 });
      return false
    }
    if (formData.description === '') {
      toastr.error('Enter description', { timeOut: 2000 });
      return false
    }

    if (Number(formData.rewardvalue) > 0) {
       if (!formData.max_reward_value || Number(formData.max_reward_value) <= 0) {
      toastr.error('Enter maximum reward value', { timeOut: 2000 });
      return false
    }
    }

    const dealValue = Number(formData.dealvalue) || 0;
    const customerValue = Number(formData.custvalue) || 0;
    // Customer + Owner are mandatory when Total Value is provided
    // Total Value is mandatory
    if (formData.dealvalue === '' || formData.dealvalue === null ||
    formData.dealvalue === undefined || isNaN(dealValue) || dealValue <= 0)
    { toastr.error('Enter comission from merchant', { timeOut: 2000 }); return false;}

    if (!viewerIsStore && Number(formData.goerone_disc_enabled) === 1) {
      const goeroneFixed = Number(formData.goerone_discount_type) === 1;
      if (customerValue <= 0 || (!goeroneFixed && customerValue > 100)) {
        toastr.error(goeroneFixed ? 'Enter the customer discount amount' : 'Customer discount must be between 0% and 100%', {timeOut: 2000});
        return false;
      }

      const goeroneSlabError = validateSlabs(formData.maxdiscount_slabs, {
        fixedCap: goeroneFixed ? customerValue : 0,
        capLabel: 'Customer discount',
      });
      if (goeroneSlabError) {
        toastr.error(goeroneSlabError, { timeOut: 3000 });
        return false;
      }
    }

    // Only stores can configure their store-sponsored discount. Admin review is read-only.
    if (viewerIsStore && Number(formData.store_disc_enabled) === 1) {
      const storeValue = Number(formData.store_disc_value) || 0;
      const storeFixed = Number(formData.store_disc_type) === 1;
      if (storeValue <= 0) {
        toastr.error('Enter store discount value', { timeOut: 2000 });
        return false;
      }
      if (!storeFixed && storeValue > 100) {
        toastr.error('Store discount must be between 0% and 100%', { timeOut: 2000 });
        return false;
      }
      const storeSlabError = validateSlabs(formData.store_maxdiscount_slabs, {
        fixedCap: storeFixed ? storeValue : 0,
        capLabel: 'Store discount value',
        prefix: 'Store discount - ',
      });
      if (storeSlabError) {
        toastr.error(storeSlabError, { timeOut: 3000 });
        return false;
      }
    }

    // Admin must switch on at least one discount (or give cashback); a store's draft can stay empty until admin fills it.
    if (!viewerIsStore && Number(formData.goerone_disc_enabled) !== 1 &&
        Number(formData.store_disc_enabled) !== 1 && !(Number(formData.rewardvalue) > 0)) {
      toastr.error('Enable at least one customer discount (GoerOne or Store) or set a cashback value', { timeOut: 3000 });
      return false;
    }

    const orderValue = Number(formData.ordervalue);

    // Minimum Order Value is mandatory
    if (formData.ordervalue === '' || formData.ordervalue === null ||
        formData.ordervalue === undefined || isNaN(orderValue) ||
        orderValue <= 0 ) {    
        toastr.error('Enter Minimum Order Value', {timeOut: 2000 }); 
        return false;}


    // A section that is switched off is sent as "no discount" so the backend can clear old values.
    const payload = { ...formData };
    const goeroneOn = !viewerIsStore && Number(payload.goerone_disc_enabled) === 1;
    // Preserve a store-created discount while an admin reviews/edits other fields.
    // Admins must never create or modify the store-funded discount.
    const storeOn = viewerIsStore
      ? Number(payload.store_disc_enabled) === 1
      : Boolean(isEditing && editMeta?.is_store_created && Number(payload.store_disc_enabled) === 1);
    // One column on the server: 0 = GoerOne, 1 = Store, 2 = Both, '' = none
    payload.discount_enabled = goeroneOn && storeOn ? 2 : goeroneOn ? 0 : storeOn ? 1 : '';
    if (!viewerIsStore) {
      // Do not send store-owned fields from the admin form. Backend must ignore these fields
      // on admin updates as well, rather than clearing them when they are absent.
      delete payload.store_disc_enabled;
      delete payload.store_disc_type;
      delete payload.store_disc_value;
      delete payload.store_maxdiscount_slabs;
    }
    delete payload.goerone_disc_enabled;
    if (!goeroneOn) {
      payload.custvalue = 0;
      payload.ownervalue = 100;          // GoerOne keeps the whole commission
      payload.goerone_discount_type = 0;
      payload.maxdiscount_slabs = [];
    } else {
      // GoerOne share is no longer asked: the remainder for percentage, none for a fixed amount
      payload.ownervalue = Number(payload.goerone_discount_type) === 1 ? 0 : 100 - (Number(payload.custvalue) || 0);
    }
    if (viewerIsStore && !storeOn) {
      payload.store_disc_value = 0;
      payload.store_maxdiscount_slabs = [];
    }
    Object.entries(payload).forEach(([key, value]) => {
    if (viewerIsStore && ADMIN_ONLY.includes(key)) return;
    if (key === 'maxdiscount_slabs' || key === 'store_maxdiscount_slabs') {
      formDataToSend.append(key, JSON.stringify(
        value.map(s => ({ to: s.to === '' ? null : Number(s.to), max_discount: Number(s.max_discount) }))
      ));
      return;
    }
    formDataToSend.append(key, value);
    });

    // console.log(formData);
    // return false;

    // if (approveAfter) {
    //     if (!(Number(formData.rewardvalue) > 0)) { toastr.error('Enter Get Cashback value to approve', { timeOut: 2000 }); return false; }
    //     if (!(Number(formData.max_reward_value) > 0)) { toastr.error('Enter Maximum cashback value to approve', { timeOut: 2000 }); return false; }
    //   }
    // for (let key in formData) {
    //   formDataToSend.append(key, formData[key]);
    // }
    // Object.entries(formData).forEach(([key, value]) => {
    //   formDataToSend.append(key, value);
    // });
    try {
      const response = await creatLoyalityReward(formDataToSend);
      if (response.status.success) {
         let message = response.message;
      if (approveAfter) {
        const approved = await approveLoyaltyReward(formData.reward_id);
        if (!approved.status?.success) {
          fetchLoyaltyCard(perPage, currentPage);
          toastr.error(`Saved, but not approved: ${approved.message}`);
          return false;                       // keep the form open
        }
        message = approved.message;
      }
        fetchLoyaltyCard(perPage, currentPage);
        setOpenTR(0)
        creatLoyalityRewardParents(formData.reward_id);
        setOpen(false)
        toastr.success(message)
        setFormData(initialFormData);

      } else {
        toastr.error(response.message)
      }
    } catch (error) {
      // console.error("Error:", error);
      toastr.error("Add Reward failed. Please try again.");
    }
  }
  const [openTR, setOpenTR] = useState(0);
  const [openTRLoading, setOpenTRLoading] = useState(false);
  const [ParentData, setParentData] = useState([]);
  const handleOpenMore = async (reward_id, key) => {
    setOpenTR(prev => (prev === key ? 0 : key));
    setOpenTRLoading(true)
    creatLoyalityRewardParents(reward_id);
  }
  const creatLoyalityRewardParents = async (reward_id) => {
    setOpenTRLoading(true)
    try {
      const response = await creatLoyalityRewardParent(reward_id);
      if (response.status.success) {
        setParentData(response.data);
        setOpenTRLoading(false)
      } else {
        toastr.error(response.message)
      }
    } catch (error) {
      // console.error("Error:", error);
      toastr.error("Add Reward failed parent. Please try again.");
    }
  }
  const handleEdit = ((item) => {
    setSelected(handleToarray(item.store_info));
    let store = (handleToarray(item.store_info));

    setFormData({
      stores_id: JSON.stringify(store),
      reward_id: item.reward_id,
      parent_id: item.parent_id,
      program_id: [item.program_id.toString()],
      is_active: item.is_active,
      reward_name: item.reward_name,
      description: item.description,
      start_date: (item.start_date) ? dayjs(item.start_date).format("YYYY-MM-DD") : null,
      end_date: (item.end_date) ? dayjs(item.end_date).format("YYYY-MM-DD") : null,
      points_required: item.points_required,
      reward_logo: item.reward_logo,
      dealtype: item.dealtype,
      rewardtype: item.rewardtype,
      dealvalue: item.dealvalue,
      ownervalue: item.ownervalue,
      custvalue: item.custvalue,
      ordervalue: item.ordervalue,
      rewardvalue: item.rewardvalue,
      maxdiscount_slabs: toEditableSlabs(item),
      goerone_disc_enabled: hasGoeroneDiscount(item) ? 1 : 0,
      goerone_discount_type: item.goerone_discount_type ?? 0,
      store_disc_enabled: hasStoreDiscount(item) ? 1 : 0,
      store_disc_type: item.store_disc_type ?? 0,
      discount_enabled: item.discount_enabled ?? '',
      store_disc_value: item.store_disc_value ?? 0,
      store_maxdiscount_slabs: toEditableSlabs(item, 'store_maxdiscount_slabs'),
      max_reward_value:item.max_reward_value,
    });
    setEditMeta({
      can_edit: item.can_edit !== false,
      lock_stores: !!item.lock_stores,
      is_store_created: !!item.is_store_created,
      approval_status: item.approval_status ?? null,
      reject_reason: item.reject_reason || '',
      created_by_store_name: item.created_by_store_name || '',
    });
    setOpen(true)
  })

  const handleToarray = ((storeString) => {
    let storeArray = storeString.split(',').map(item => {
      const [id, name] = item.trim().split(':');
      return {
        id: parseInt(id),
        store_name: name.trim()
      };
    });
    return storeArray
  })

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [storeOptions, setStoreOptions] = useState([]);

  const filteredOptions = storeOptions.filter((store) => {
    const storeName = String(store?.store_name || '');
    const storeId = store?.id ?? store?.store_id;
    return (
      storeName.toLowerCase().includes(search.toLowerCase()) &&
      !selected.some((item) => String(item.id) === String(storeId))
    );
  });

  useEffect(() => {
    const keyword = search.trim();
    let cancelled = false;

    const loadStores = async () => {
      if (keyword.length > 2) {
        const rows = await fetchStore(keyword);
        if (!cancelled) setStoreOptions(rows);
      } else {
        setStoreOptions(storelist);
      }
    };

    if (isFocused) loadStores();
    else if (!keyword) setStoreOptions(storelist);

    return () => {
      cancelled = true;
    };
  }, [search, isFocused, storelist]);

  const addStore = (store) => {
    const selectedStore = {
      id: store.id ?? store.store_id,
      store_name: store.store_name ?? store.name ?? '',
    };
    const newSet = [...selected, selectedStore]
    setSelected(newSet);
    setFormData(prev => ({
      ...prev,
      ['stores_id']: JSON.stringify(newSet)
    }));
    setSearch("");
  };

  const removeStore = (id) => {
    const newSet = selected.filter((store) => store.id !== id)
    setSelected(newSet);
    setFormData(prev => ({
      ...prev,
      ['stores_id']: JSON.stringify(newSet)
    }));
  };

  const canDecide = !viewerIsStore && isEditing && editMeta.is_store_created && Number(editMeta.approval_status) !== 1;

  // Shared max-discount slab editor used by both the GoerOne and the Store discount sections.
  const renderSlabs = (key, { label, required = false, capValue = 0, capNote = '' }) => {
    const slabs = formData[key] || [];
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
          <span className="w-36 shrink-0">Order value</span>
          <span>Max discount</span>
        </div>
        {slabs.map((slab, index) => {
          const isLast = index === slabs.length - 1;
          const from = index === 0 ? 0 : slabs[index - 1].to || 0;
          return (
            <div key={index} className="flex items-center gap-2 mb-2">
              <span className="text-sm text-gray-600 w-14 shrink-0 text-right">{from} –</span>
              {isLast ? (
                <span className="text-sm text-gray-600 w-20 shrink-0">and above</span>
              ) : (
                <input
                  type="number" min="0" placeholder="Up to"
                  value={slab.to}
                  onChange={(e) => updateSlab(index, 'to', e.target.value, key)}
                  className={`w-20 ${slabInputCls}`}
                />
              )}
              <input
                type="number" min="0"
                max={capValue || undefined}
                placeholder="Max discount"
                value={slab.max_discount}
                onChange={(e) => updateSlab(index, 'max_discount', e.target.value, key)}
                className={`flex-1 min-w-0 ${slabInputCls}`}
              />
              {slabs.length > 1 && (
                <button
                  type="button"
                  aria-label="Remove slab"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeSlab(index, key);
                  }}
                  className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700"
                >
                  ✕
                </button>
              )}
            </div>
          );
        })}
        <button type="button" onClick={() => addSlab(key)} className="text-sm text-[#f4a406] hover:text-[#d89005] font-medium">+ Add slab</button>
        {capNote && <p className="mt-1 text-xs text-gray-500">{capNote}</p>}
      </div>
    );
  };

  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-4">

      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <form onSubmit={handleSubmit} className="w-full max-w-4xl">
            <div className="bg-white rounded-xl shadow-xl w-full max-h-[95vh] flex flex-col">
              <div className="flex justify-between items-center border-b px-6 py-4">
                <h3 className="text-lg font-semibold">{readOnly ? 'View Loyalty Reward' : isEditing ? 'Edit Loyalty Reward' : 'Add Loyalty Reward'}</h3>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setOpen(false)}
                  className="text-gray-500 hover:text-gray-700 rounded-lg p-1 focus:outline-none focus:ring-2 focus:ring-[#f4a406]"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-5 space-y-5 overflow-y-auto flex-grow">
                {/* Store viewing an admin-created reward */}
                {readOnly && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    This reward has been created by admin. If you want to change it, please contact admin.
                  </div>
                )}
                {/* Store adding / editing its own reward */}
                {viewerIsStore && !readOnly && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    {isEditing
                      ? 'Your reward will become inactive after you save this edit. It goes live again once admin approves it.'
                      : 'Rewards created by a store are inactive by default. Your reward goes live after admin approves it.'}
                  </div>
                )}
                {/* Rejected: show why (store can fix and resubmit) */}
                {isEditing && editMeta.approval_status === 2 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    <div className="font-medium">Rejected by admin</div>
                    {editMeta.reject_reason && <div>Reason: {editMeta.reject_reason}</div>}
                    {viewerIsStore && !readOnly && <div className="mt-1">Update the reward and save it to submit it for approval again.</div>}
                  </div>
                )}
                {/* Admin opening a store-created reward */}
                {lockStores && (
                  <div className="rounded-lg border border-purple-200 bg-purple-50 px-4 py-3 text-sm text-purple-800">
                    This reward was created by store{editMeta.created_by_store_name ? ` "${editMeta.created_by_store_name}"` : ''}. The store assignment can't be changed, and its status is changed from the Reward Approvals page.
                    Store discount values are managed only by the store. You can review them here, but cannot add or edit them. Approve or reject the reward after reviewing.
                  </div>
                )}

                <fieldset disabled={readOnly} className={`space-y-5 border-0 p-0 m-0 min-w-0 ${readOnly ? 'opacity-70 pointer-events-none' : ''}`}>

                  {/* ---------- Basics ---------- */}
                  <Section title="Basics">
                    <div className="space-y-4">
                      {/* Store picker (admin only; stores are always mapped to their own store) */}
                      <div ref={wrapperRef} className={viewerIsStore ? 'hidden' : 'relative'}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Stores <span className="text-red-500">*</span></label>
                        <div className="border border-gray-300 rounded-xl p-2 min-h-[44px] flex flex-wrap items-center gap-1 focus-within:ring-2 focus-within:ring-[#f4a406] focus-within:border-[#f4a406]">
                          {selected.map((store, key) => (
                            <span
                              key={store.id + '-' + key}
                              className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-sm flex items-center"
                            >
                              {store.store_name}
                              <button
                                type="button"
                                aria-label={`Remove ${store.store_name}`}
                                disabled={lockStores}
                                onClick={() => removeStore(store.id)}
                                className={lockStores ? 'hidden' : 'ml-1 text-blue-500 hover:text-blue-700'}
                              >
                                &times;
                              </button>
                            </span>
                          ))}
                          <input
                            id="store-input"
                            type="text"
                            className="flex-grow min-w-[10rem] outline-none p-1 text-sm disabled:bg-transparent disabled:cursor-not-allowed"
                            disabled={lockStores}
                            placeholder={lockStores ? 'Store assignment is locked' : 'Search by store/events name, email or mobile...'}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onFocus={() => setIsFocused(true)}
                          />
                        </div>
                        {isFocused && filteredOptions.length > 0 && (
                          <ul className="absolute z-20 left-0 right-0 mt-1 border border-gray-200 rounded-xl max-h-60 overflow-y-auto bg-white shadow-lg">
                            {filteredOptions.map((store) => (
                              <li
                                key={store.id ?? store.store_id}
                                className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
                                onClick={() => addStore(store)}
                              >
                                {store.store_name ?? store.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Reward name" required>
                          <div className="relative">
                            <input
                              type="text"
                              name="reward_name"
                              value={formData.reward_name || ""}
                              onChange={handleChange}
                              placeholder="Reward name"
                              className={`${inputCls} pl-10`}
                            />
                            <Gift className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                          </div>
                        </Field>
                        <Field label="Program name" required>
                          <div className="relative">
                            <MultiSelect
                              loyalty_program={loyalty_program}
                              formData={formData}
                              setFormData={setFormData}
                            />
                            <ListCheck className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                          </div>
                        </Field>
                        <Field label="Status">
                          <select
                            name="is_active"
                            value={viewerIsStore ? "0" : (formData.is_active ?? "")}
                            onChange={handleChange}
                            disabled={viewerIsStore || editMeta?.approval_status === 0}
                            className={inputCls}
                          >
                            <option value="" disabled>Select</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                          </select>
                        </Field>
                        <Field label="Min. order value" required>
                          <input
                            type="number"
                            name="ordervalue"
                            value={formData.ordervalue || ""}
                            onChange={handleChange}
                            placeholder="Minimum order value"
                            className={inputCls}
                          />
                        </Field>
                        <Field label="Start date">
                          <DateInput
                            valueFormat="DD-MM-YYYY"
                            placeholder="Pick start date"
                            value={formData.start_date}
                            onChange={(date) => {
                              handleChange(date, 'start_date');
                              // reset toDate if it's before the new fromDate
                              if (formData.end_date && date && formData.end_date < date) {
                                handleChange(null, 'end_date');
                              }
                            }}
                            leftSection={<CalendarDays size={18} className="text-gray-400" />}
                            className="w-full rounded-xl"
                          />
                        </Field>
                        <Field label="End date">
                          <DateInput
                            valueFormat="DD-MM-YYYY"
                            minDate={formData.start_date || undefined}
                            placeholder="Pick end date"
                            value={formData.end_date}
                            onChange={(date) => {
                              handleChange(date, 'end_date');
                            }}
                            leftSection={<CalendarDays size={18} className="text-gray-400" />}
                            className="w-full rounded-xl"
                          />
                        </Field>
                      </div>
                    </div>
                  </Section>

                  {/* ---------- Merchant commission ---------- */}
                  <Section title="Merchant commission" hint="What GoerOne earns from the merchant on each order.">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                      <Field label="Deals in">
                        <Segmented
                          name="dealtype"
                          value={formData.dealtype}
                          onChange={handleChange}
                          options={[{ value: '0', label: 'Percentage' }, { value: '1', label: 'Fixed' }]}
                        />
                      </Field>
                      <Field label="Total commission from merchant" required>
                        <input
                          type="number"
                          name="dealvalue"
                          value={formData.dealvalue || ""}
                          onChange={handleChange}
                          placeholder="Total commission"
                          className={inputCls}
                        />
                      </Field>
                    </div>
                  </Section>

                  {/* ---------- Customer discounts ---------- */}
                  <Section title="Customer discounts" hint={viewerIsStore ? "Configure the discount funded by your store." : "GoerOne discount is admin-managed. Store discount is view-only and can only be managed by the store."}>
                    <div className={`grid grid-cols-1 gap-4 ${viewerIsStore ? '' : 'md:grid-cols-2'}`}>
                      {!viewerIsStore && (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                          <Switch
                            checked={Number(formData.goerone_disc_enabled) === 1}
                            onChange={(e) => setFormData(prev => ({ ...prev, goerone_disc_enabled: e.target.checked ? 1 : 0 }))}
                            label="By GoerOne"
                            hint="Paid out of the merchant commission."
                          />
                          {Number(formData.goerone_disc_enabled) === 1 && (
                            <>
                              <Field label="Discount in">
                                <Segmented
                                  name="goerone_discount_type"
                                  value={formData.goerone_discount_type}
                                  onChange={handleChange}
                                  options={[{ value: '0', label: 'Percentage' }, { value: '1', label: 'Fixed' }]}
                                />
                              </Field>
                              <Field label={`Customer discount ${Number(formData.goerone_discount_type) === 1 ? '(amount)' : '(%)'}`} required>
                                <input
                                  type="number"
                                  name="custvalue"
                                  value={formData.custvalue || ""}
                                  onChange={handleChange}
                                  placeholder={Number(formData.goerone_discount_type) === 1 ? 'e.g. 100' : 'e.g. 60'}
                                  className={inputCls}
                                />
                              </Field>
                              {renderSlabs('maxdiscount_slabs', {
                                label: 'Maximum discount (by order value)',
                                required: true,
                                capValue: Number(formData.goerone_discount_type) === 1 ? Number(formData.custvalue) || 0 : 0,
                                capNote: Number(formData.goerone_discount_type) === 1 ? 'For fixed discounts, max discount cannot exceed the Customer discount amount.' : '',
                              })}
                            </>
                          )}
                        </div>
                      )}

                      {viewerIsStore ? (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                          <Switch
                            checked={Number(formData.store_disc_enabled) === 1}
                            onChange={(e) => setFormData(prev => ({ ...prev, store_disc_enabled: e.target.checked ? 1 : 0 }))}
                            label="By You"
                            hint="Paid by your store. Only your store can configure this discount."
                          />
                          {Number(formData.store_disc_enabled) === 1 && (
                            <>
                              <Field label="Discount in">
                                <Segmented
                                  name="store_disc_type"
                                  value={formData.store_disc_type}
                                  onChange={handleChange}
                                  options={[{ value: '0', label: 'Percentage' }, { value: '1', label: 'Fixed' }]}
                                />
                              </Field>
                              <Field label={`Customer discount ${Number(formData.store_disc_type) === 1 ? '(amount)' : '(%)'}`} required>
                                <input
                                  type="number"
                                  min="0"
                                  name="store_disc_value"
                                  value={formData.store_disc_value || ""}
                                  onChange={handleChange}
                                  placeholder="Store discount value"
                                  className={inputCls}
                                />
                              </Field>
                              {renderSlabs('store_maxdiscount_slabs', {
                                label: 'Customer maximum discount (by order value)',
                                required: true,
                                capValue: Number(formData.store_disc_type) === 1 ? Number(formData.store_disc_value) || 0 : 0,
                                capNote: Number(formData.store_disc_type) === 1 ? 'For fixed discounts, max discount cannot exceed the Store discount value.' : '',
                              })}
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
                          <Switch
                            checked={Number(formData.store_disc_enabled) === 1}
                            disabled
                            label="Store discount"
                            hint="Only editable or addable by the store. Admin can view this setting but cannot change it."
                          />
                          {Number(formData.store_disc_enabled) === 1 && (
                            <div className="border-t border-gray-200 pt-3 text-sm text-gray-700 space-y-1">
                              <p><span className="font-medium">Discount type:</span> {Number(formData.store_disc_type) === 1 ? 'Fixed amount' : 'Percentage'}</p>
                              <p><span className="font-medium">Discount value:</span> {formData.store_disc_value || 0}{Number(formData.store_disc_type) === 1 ? '' : '%'}</p>
                              <div className="mt-2">
                                <p className="font-medium">Maximum discount slabs</p>
                                {(formData.store_maxdiscount_slabs || []).map((slab, index) => (
                                  <p key={index} className="text-xs text-gray-600">
                                    {index === 0 ? '₹0' : `Above ₹${formData.store_maxdiscount_slabs[index - 1]?.to || 0}`} – {slab.to === '' || slab.to == null ? 'and above' : `₹${slab.to}`}: max discount ₹{slab.max_discount || 0}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Section>

                  {/* ---------- Cashback (admin only) ---------- */}
                  {!viewerIsStore && (
                    <Section title="Cashback">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <Field label="Cashback in">
                          <Segmented
                            name="rewardtype"
                            value={formData.rewardtype}
                            onChange={handleChange}
                            options={[{ value: '0', label: 'Percentage' }, { value: '1', label: 'Fixed' }]}
                          />
                        </Field>
                        <Field label="Cashback value">
                          <input
                            type="number"
                            name="rewardvalue"
                            value={formData.rewardvalue || ""}
                            onChange={handleChange}
                            placeholder="Enter value % or fixed"
                            className={inputCls}
                          />
                        </Field>
                        <Field label="Maximum cashback value">
                          <input
                            type="number"
                            min="0"
                            name="max_reward_value"
                            value={formData.max_reward_value || ""}
                            onChange={handleChange}
                            placeholder="Maximum cashback value"
                            className={inputCls}
                          />
                        </Field>
                      </div>
                    </Section>
                  )}

                  {/* ---------- Details ---------- */}
                  <Section title="Details">
                    <div className="space-y-4">
                      <Field label="Description" required>
                        <textarea
                          name="description"
                          rows={3}
                          value={formData.description || ""}
                          onChange={handleChange}
                          placeholder="Description"
                          className={inputCls}
                        />
                      </Field>
                      <div className="flex items-start gap-4">
                        <Field label="Reward image" className="flex-1 min-w-0">
                          <input
                            type="file"
                            ref={fileInputRef}
                            name="reward_logo"
                            accept="image/*"
                            onChange={handleChangeImg}
                            className="block w-full text-sm text-gray-700 border border-gray-300 rounded-xl file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-semibold file:bg-[#f4a406] file:text-white hover:file:bg-yellow-600"
                          />
                        </Field>
                        {formData.reward_logo && (
                          <img
                            src={
                              typeof formData.reward_logo === 'string'
                                ? `${formData.reward_logo}`
                                : formData.reward_logo_preview
                            }
                            alt="Reward preview"
                            className="h-20 w-20 shrink-0 rounded-xl border object-cover"
                          />
                        )}
                      </div>
                    </div>
                  </Section>
                </fieldset>
              </div>

              <div className="flex flex-wrap justify-end gap-2 border-t px-6 py-3">
                {canDecide && Number(editMeta.approval_status) !== 2 && (
                  <button
                    type="button"
                    onClick={() => setRejectTarget({ reward_id: formData.reward_id, reward_name: formData.reward_name,
                                                      created_by_store_name: editMeta.created_by_store_name })}
                    className="px-5 py-2 rounded-xl border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                  >
                    Reject
                  </button>
                )}
                {readOnly ? (
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="px-5 py-2 bg-gray-200 text-gray-800 rounded-xl hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="px-5 py-2 bg-[#f4a406] text-white rounded-xl hover:bg-[#d89005] transition-colors"
                  >
                    {viewerIsStore ? 'Save & submit for approval' : 'Save'}
                  </button>
                )}
                {canDecide && (
                  <button
                    type="button"
                    onClick={(e) => handleSubmit(e, { approveAfter: true })}
                    className="px-5 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors"
                  >
                    Save & Approve
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      )}

      {rejectTarget && (
        <RejectRewardModal target={rejectTarget} onClose={() => setRejectTarget(null)}
          onRejected={() => { setRejectTarget(null); setOpen(false); setFormData(initialFormData); fetchLoyaltyCard(perPage, currentPage); }} />
      )}

      <div className="w-full space-y-4">

        {/* Header */}
        <div className="rounded-xl p-4 text-white shadow-lg bg-gradient-to-r from-[#f4a406] to-[#d89005]">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
              <Gift size={22} />
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight">Loyalty Reward</h2>
              <p className="text-sm text-white/90">
                {keyword ? `Showing results for “${keyword}”` : 'Create and manage rewards for your stores'}
              </p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                aria-expanded={showFilters}
                title="Filter"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors"
              >
                <Filter size={16} />
                {showFilters ? 'Hide filters' : 'Filters'}
                {activeFilters > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-white text-[#d89005] text-xs font-bold inline-flex items-center justify-center">
                    {activeFilters}
                  </span>
                )}
              </button>
              <button type="button" onClick={handleOpen} className="px-4 py-2 rounded-xl bg-white text-[#d89005] text-sm font-semibold hover:bg-amber-50 transition-colors">
                Add New
              </button>
            </div>
          </div>
        </div>

        {/* Filters (collapsed by default) */}
        {showFilters && (
          <form onSubmit={handleApplyFilters} className="bg-white shadow-lg rounded-2xl p-4">
            <div className={`grid grid-cols-1 gap-3 items-end ${viewerIsStore ? 'lg:grid-cols-[2fr_1fr_auto]' : 'lg:grid-cols-[2fr_1fr_1fr_auto]'}`}>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                <div className="relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="keyword"
                    value={filterDraft.keyword}
                    onChange={handleDraft}
                    placeholder="Reward name or store name"
                    autoComplete="off"
                    className="w-full pl-9 pr-9 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#f4a406] focus:outline-none"
                  />
                  {filterDraft.keyword && (
                    <button
                      type="button"
                      onClick={() => setFilterDraft((prev) => ({ ...prev, keyword: '' }))}
                      title="Clear"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>
              {!viewerIsStore && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Created by</label>
                  <select name="createdBy" value={filterDraft.createdBy} onChange={handleDraft} className="w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#f4a406] focus:outline-none">
                    <option value="all">All</option>
                    <option value="admin">Admin</option>
                    <option value="store">Store</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select name="status" value={filterDraft.status} onChange={handleDraft} className="w-full px-3 py-2 border rounded-xl text-sm focus:ring-2 focus:ring-[#f4a406] focus:outline-none">
                  <option value="all">All statuses</option>
                  <option value="pending">Pending approval</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="inline-flex items-center justify-center gap-1 py-2 px-4 bg-[#f4a406] text-white rounded-xl hover:bg-[#d89005] transition-colors text-sm">
                  <Search size={16} /> Search
                </button>
                <button type="button" onClick={handleResetFilters} title="Reset filters" className="py-2 px-3 border rounded-xl text-gray-600 hover:bg-gray-50 transition-colors">
                  <RotateCcw size={16} />
                </button>
              </div>
            </div>
          </form>
        )}

        <div className="overflow-auto bg-white shadow-lg rounded-2xl p-3">

          <table className="w-full min-w-[920px] text-sm text-left">
            <thead className="text-gray-700 bg-gray-100">
              <tr>
                <th className="p-3">ID</th>
                <th className="p-3">Program Name</th>
                <th className="p-3">Reward name</th>
                <th className="p-3">Description</th>
                <th className="p-3">Store</th>
                {/* <th className="p-3">Points Required</th> */}
                <th className="p-3">Status</th>
                <th className="p-3">Created On</th>
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <>
                  <tr>
                    <td className="p-3" colSpan={8}>
                      <ShimmerLoader />
                      <ShimmerLoader />
                      <ShimmerLoader />
                    </td>
                  </tr>
                </>
              ) : (
                <>
                  {loyalty_card.length > 0 ? (
                    loyalty_card.map((item, key) => (
                      <Fragment key={key}>
                        <tr className="hover:bg-gray-50 align-top border-l-4 border-[#f4a406]">
                          <td className="p-3 text-gray-500">#{item.reward_id}</td>
                          <td className="p-3"><ProgramChips programs={item.program} /></td>
                          <td className="p-3"><div className="font-semibold text-gray-900">{item.reward_name}</div><CreatedBy item={item} /></td>
                          <td className="p-3"><div className="max-w-[260px] line-clamp-2 text-gray-600" title={item.description}>{item.description}</div></td>
                          <td className="p-3"><StoreChips info={item.store_info} /></td>
                          {/* <td className="p-3">{item.points_required}</td> */}
                          <td className="p-3"><RewardStatus item={item} /></td>
                          <td className="p-3 whitespace-nowrap text-gray-600">{item.createdDate}</td>
                          <td className="p-3 text-center space-x-2" nowrap="true">
                            {viewerIsStore && item.is_store_created &&
                              Number(item.approval_status) === 1 &&
                              Number(item.is_active) === 1 &&
                              (!viewerIsStore || item.can_edit) && (
                                <button
                                  type="button"
                                  onClick={() => handleDeactivate(item)}
                                  className="inline-flex items-center gap-1 text-red-600 hover:text-red-800"
                                  title="Deactivate reward"
                                >
                                  <EyeOff size={16} />
                                  Deactivate
                                </button>
                              )}
                            {item.program.length > 1 ? (
                              <>
                                <button onClick={() => handleEdit(item)} className={ACTION_BTN}>
                                  {item.can_edit === false ? <Eye size={16} /> : <Edit size={16} />}
                                </button>
                                <button onClick={() => handleOpenMore(item.reward_id, key + 1)} className={ACTION_BTN}>
                                  {openTR === (key + 1) ? (
                                    <span>Less({item.program.slice(1).length})</span>
                                  ) : (
                                    <span>More({item.program.slice(1).length})</span>
                                  )}
                                </button>
                              </>
                            ) : (

                              <button onClick={() => handleEdit(item)} className={ACTION_BTN}>
                                {item.can_edit === false ? <Eye size={16} /> : <Edit size={16} />}
                              </button>
                            )}
                          </td>
                        </tr>
                        {openTRLoading && openTR === (key + 1) ? (
                          <>
                            <tr>
                              <td className="p-3" colSpan={8}>
                                <ShimmerLoader />
                              </td>
                            </tr>
                          </>
                        ) : (
                          <>
                            {item.program.length > 1 && openTR === (key + 1) && ParentData.map((items, keys) => (
                              <tr key={keys + '=' + key} className="bg-blue-50" id={keys + '=' + key}>
                                <td className="p-3">{items.reward_id} </td>
                                <td className="p-3">{items.program_name}</td>
                                <td className="p-3">{items.reward_name} <CreatedBy item={items} /></td>
                                <td className="p-3">{items.description}</td>
                                <td className="p-3">{items.points_required}</td>
                                <td className="p-3"><RewardStatus item={items} /></td>
                                <td className="p-3" nowrap="true">{items.createdDate} </td>
                                <td className="p-3 text-center space-x-2">
                                
                                  <button onClick={() => handleEdit(items)} className={ACTION_BTN}>
                                    {items.can_edit === false ? <Eye size={16} /> : <Edit size={16} />}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </>
                        )}

                      </Fragment>
                    ))
                  ) : (
                    <tr>
                      <td className="p-8 text-center text-gray-500" colSpan={8}>
                        No record found
                      </td>
                    </tr>
                  )}

                </>
              )}

            </tbody>
          </table>
          <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
        </div>
      </div>
    </div>
  );
}